// ══════════════════════════════════════════════════════════════════
// CartHub Dashboard — App Shell
// Routing, sidebar, topbar, stato globale.
// I componenti sono in src/components/, lo shared in src/shared.js.
//
// MANIFEST COMPONENTI (per verifica integrità):
//   Login, LanguageSelector, Carts, Stats, StatsPopupPage, Brands, PriceAlerts, Settings, Subscription,
//   TrialMessagingContent, TrialWelcomeModal, TrialTrialBar, MerchantPlanPills, BillingAttentionBanner
// Se uno manca dal filesystem → il build fallisce → errore esplicito.
// ══════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, styles, Ic, av } from "./shared";
import { dateLocaleForAppLang } from "./i18n";

// Componenti — ciascuno in file separato per evitare perdite accidentali
import Login        from "./components/Login";
import LanguageSelector from "./components/LanguageSelector";
import Carts        from "./components/Carts";
import Stats        from "./components/Stats";
import StatsPopupPage from "./components/StatsPopupPage";
import Brands       from "./components/Brands";
import PriceAlerts  from "./components/PriceAlerts";
import Settings     from "./components/Settings";
import Subscription from "./components/Subscription";
import TrialWelcomeModal, { hasSeenTrialWelcomeModal, markTrialWelcomeModalSeen } from "./components/TrialWelcomeModal";
import TrialTrialBar from "./components/TrialTrialBar";
import MerchantPlanPills from "./components/MerchantPlanPills";
import BillingAttentionBanner from "./components/BillingAttentionBanner";

function isMerchantTrialActive(shop) {
  if (!shop) return false;
  if (shop.commercial_state === "standby" || shop.commercial_state === "closed") return false;
  if (shop.plan !== "trial") return false;
  if (shop.is_agency_managed) return true;
  if (!shop.trial_expires_at) return true;
  return new Date(shop.trial_expires_at) > new Date();
}

// ── Navigazione sidebar (etichette da common.json `nav.*`) ──
const STATS_SUBMENU_PAGE_IDS = new Set(["stats", "price-alert-stats", "stats-popup"]);

const NAV = [
  { id: "carts", icon: "cart", s: "main" },
  {
    kind: "statsSubmenu",
    s: "main",
    icon: "chart",
    labelKey: "statistics",
    children: [
      { id: "stats", labelKey: "stats_carts" },
      { id: "price-alert-stats", labelKey: "stats_price_alert" },
      { id: "stats-popup", labelKey: "stats_popup" },
    ],
  },
  { id: "brands", icon: "brand", s: "main" },
  { id: "subscription", icon: "wallet", s: "config" },
  { id: "settings", icon: "settings", s: "config" },
];

const NAV_SECTIONS = [
  { sid: "main", labelKey: "nav.section_main" },
  { sid: "config", labelKey: "nav.section_config" },
];

function navItemLabel(id, t) {
  const k = id.replace(/-/g, "_");
  return t(`nav.${k}`);
}

function pageTitleKey(page) {
  return page.replace(/-/g, "_");
}

export default function App() {
  const { t, i18n } = useTranslation("common");
  // Gestione impersonation da dashboard agenzia
  const [auth,setAuth]=useState(()=>{
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get("impersonate");
    const impShop  = params.get("shop");
    if(impToken && impShop){
      try {
        localStorage.setItem("carthub_token", impToken);
        localStorage.setItem("carthub_shop", decodeURIComponent(impShop));
        window.history.replaceState({}, "", window.location.pathname);
      } catch(e) {}
    }
    return !!localStorage.getItem("carthub_token");
  });
  const [shop, setShop]=useState(()=>{ try{ return JSON.parse(localStorage.getItem("carthub_shop")); }catch{ return null; } });
  const [page,setPage]=useState("carts");
  const [refreshing,setRefreshing]=useState(false);
  const [cartStats,setCartStats]=useState({total:0,pending:0,recovered:0,organic:0,rate:0});
  /** Solo iscrizioni con status `active` — badge sidebar Price Alert */
  const [priceAlertActiveCount,setPriceAlertActiveCount]=useState(0);
  const [brandsCount,setBrandsCount]=useState(0);
  const [pluginInfo,setPluginInfo]=useState({version:"...",updated_at:""});
  useEffect(()=>{
    fetch("https://app.carthub.cloud/api/plugin/info")
      .then(r=>r.json())
      .then(d=>{ if(d?.version) setPluginInfo({version:d.version, updated_at:d.last_updated||""}); })
      .catch(()=>{});
  },[]);
  const [navOpen, setNavOpen] = useState(false);
  /** Da legenda statistiche PA → tab Impostazioni «Personalizza Price Alert» */
  const [settingsTabHint, setSettingsTabHint] = useState(null);
  const [trialWelcomeOpen, setTrialWelcomeOpen] = useState(false);
  const logout=()=>{ localStorage.removeItem("carthub_token"); localStorage.removeItem("carthub_shop"); setAuth(false); };
  const shopId = shop?.id;

  const isStatsSubPage = STATS_SUBMENU_PAGE_IDS.has(page);
  const [statsSubmenuOpen, setStatsSubmenuOpen] = useState(isStatsSubPage);

  useEffect(() => {
    if (STATS_SUBMENU_PAGE_IDS.has(page)) setStatsSubmenuOpen(true);
  }, [page]);

  const mergeShop = useCallback((partial) => {
    setShop((prev) => {
      const updated = { ...(prev || {}), ...partial };
      localStorage.setItem('carthub_shop', JSON.stringify(updated));
      return updated;
    });
  }, []);

  /** Allinea stato shop dopo checkout/portale Stripe (banner billing, api_key ruotata). */
  const applySubscriptionPayload = useCallback(
    (d) => {
      if (!d || d.error) return;
      mergeShop({
        plan: d.plan,
        billing_interval: d.billing_interval,
        current_period_end: d.current_period_end,
        carts_limit: d.carts_limit,
        trial_expires_at: d.trial_expires_at,
        commercial_state: d.commercial_state,
        integration_active: d.integration_active,
        ...(d.stripe_subscription_id != null ? { stripe_subscription_id: d.stripe_subscription_id } : {}),
        ...(d.api_key ? { api_key: d.api_key } : {}),
      });
    },
    [mergeShop]
  );

  const deactivateSilentMode = async () => {
    if(!window.confirm(t("confirm.silentDeactivate"))) return;
    try {
      const res = await api(`/settings/${shopId}/silent-mode`, { method:'DELETE' });
      if(res && !res.error){
        mergeShop({ silent_until: null });
      }
    } catch(e) {}
  };

  const toggleEmailPause = async () => {
    const pausing = !shop?.email_paused;
    if(pausing && !window.confirm(t("confirm.emailPause"))) return;
    try {
      const res = await api(`/settings/${shopId}/email-pause`, { method: pausing ? 'POST' : 'DELETE' });
      if(res && !res.error){
        mergeShop({ email_paused: pausing });
      }
    } catch(e) {}
  };

  // Gestisce redirect da Stripe checkout
  useEffect(()=>{
    const params = new URLSearchParams(window.location.search);
    if(params.get("checkout") === "success"){
      window.history.replaceState({}, "", "/");
      setPage("subscription");
      setRefreshing(true);
      api(`/stripe-merchant/subscription`).then(applySubscriptionPayload).finally(()=>setRefreshing(false));
    }
    if(params.get("checkout") === "cancelled"){
      window.history.replaceState({}, "", "/");
      setPage("subscription");
    }
  },[applySubscriptionPayload]);

  // Se auth ma shop mancante → logout automatico (sessione corrotta)
  useEffect(()=>{ 
    if(auth && !shopId) logout();
  }, []);
  // Carica contatori per topbar e sidebar badge.
  // Polling ogni 60s + refresh immediato al cambio pagina per tenere
  // il badge "pending" della sidebar sempre aggiornato.
  const refreshStats = useCallback(()=>{
    if(!shopId) return;
    api(`/stats/${shopId}`).then(d=>{
      if(d && !d.error) setCartStats({
        total: d.total_carts||0,
        pending: (d.total_carts||0)-(d.recovered_carts||0)-(d.organic_carts||0),
        recovered: d.recovered_carts||0,
        organic: d.organic_carts||0,
        rate: d.recovery_rate||0
      });
    }).catch(()=>{});
    api(`/price-alerts/${shopId}/stats`).then(pa=>{
      if(pa && !pa.error) setPriceAlertActiveCount(pa.active||0);
    }).catch(()=>{});
  },[shopId]);

  useEffect(()=>{
    refreshStats();
    api(`/brands/${shopId}`).then(d=>{ if(Array.isArray(d)) setBrandsCount(d.length); }).catch(()=>{});
    const interval = setInterval(refreshStats, 60000);
    return ()=>clearInterval(interval);
  },[shopId, refreshStats]);

  /** silent_until / email_paused / api_key: allineamento al backend (silent attivato al primo carrello webhook). */
  useEffect(() => {
    if (!auth || !shopId) return;
    let cancelled = false;
    const pullShopProfile = () => {
      api(`/shops/${shopId}`).then((d) => {
        if (cancelled || !d || d.error) return;
        mergeShop({
          silent_until: d.silent_until ?? null,
          email_paused: !!d.email_paused,
          ...(d.api_key ? { api_key: d.api_key } : {}),
          ...(typeof d.integration_active === "boolean" ? { integration_active: d.integration_active } : {}),
        });
      }).catch(() => {});
    };
    pullShopProfile();
    const interval = setInterval(pullShopProfile, 90000);
    const onVis = () => {
      if (document.visibilityState === "visible") pullShopProfile();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [auth, shopId, mergeShop]);

  // Refresh stats al cambio tab per feedback immediato
  useEffect(()=>{ refreshStats(); },[page, refreshStats]);

  useEffect(()=>{
    if(!navOpen) return;
    const onKey = (e)=>{ if(e.key==="Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return ()=>window.removeEventListener("keydown", onKey);
  },[navOpen]);

  useEffect(()=>{
    const onResize = ()=>{ if(window.innerWidth>900) setNavOpen(false); };
    window.addEventListener("resize", onResize);
    return ()=>window.removeEventListener("resize", onResize);
  },[]);

  useEffect(() => {
    if (!isMerchantTrialActive(shop)) return;
    if (hasSeenTrialWelcomeModal()) return;
    setTrialWelcomeOpen(true);
    // Intenzionale: dipendenze primitive del profilo trial, non l'oggetto shop intero (evita ri-aperture su refresh stats).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop?.id, shop?.plan, shop?.trial_expires_at, shop?.commercial_state, shop?.is_agency_managed]);

  const goToSubscription = useCallback(() => {
    markTrialWelcomeModalSeen();
    setTrialWelcomeOpen(false);
    setPage("subscription");
    setNavOpen(false);
  }, []);

  if(!auth) return <><style>{styles}</style><Login onLogin={()=>window.location.reload()}/></>;

  if(refreshing) return (
    <>
      <style>{styles}</style>
      <div className="app-loading-screen">
        <div className="app-loading-spinner" aria-hidden />
        <div>{t("loading.planUpdate")}</div>
      </div>
    </>
  );

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <div
          className={`sidebar-backdrop${navOpen ? " is-open" : ""}`}
          onClick={()=>setNavOpen(false)}
          aria-hidden={!navOpen}
        />
        {/* ── SIDEBAR ── */}
        <aside className={`sidebar${navOpen ? " is-open" : ""}`}>
          <div className="sidebar-head">
            <div className="sidebar-logo" aria-label="CartHub">Cart<span>Hub</span></div>
            <div className="sidebar-sub">Cart Recovery AI</div>
          </div>
          <nav className="sidebar-nav">
            {NAV_SECTIONS.map(({ sid, labelKey })=>(
              <div key={sid}>
                <div className="nav-section">{t(labelKey)}</div>
                {NAV.filter((n) => n.s === sid)
                  .filter((n) => !(n.id === "subscription" && shop?.is_agency_managed))
                  .map((item) => {
                    if (item.kind === "statsSubmenu") {
                      const hasActiveChild = item.children.some((c) => c.id === page);
                      return (
                        <div key="stats-submenu" className="nav-group">
                          <div
                            role="button"
                            tabIndex={0}
                            aria-expanded={statsSubmenuOpen}
                            className={`nav-item nav-item--parent${statsSubmenuOpen ? " is-open" : ""}${hasActiveChild && !statsSubmenuOpen ? " has-active-child" : ""}`}
                            onClick={() => setStatsSubmenuOpen((o) => !o)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setStatsSubmenuOpen((o) => !o);
                              }
                            }}
                          >
                            <span style={{ color: "#9ca3af", display: "flex", alignItems: "center", flexShrink: 0 }}>{Ic[item.icon]}</span>
                            <span className="nav-parent-label">{t(`nav.${item.labelKey}`)}</span>
                            <span className="nav-group-chevron" aria-hidden>
                              {Ic.chevronDown}
                            </span>
                          </div>
                          {statsSubmenuOpen && (
                            <div className="nav-submenu" role="group" aria-label={t(`nav.${item.labelKey}`)}>
                              {item.children.map((child) => (
                                <div
                                  key={child.id}
                                  className={`nav-item nav-item--sub${page === child.id ? " active" : ""}`}
                                  onClick={() => {
                                    setPage(child.id);
                                    setNavOpen(false);
                                  }}
                                >
                                  {t(`nav.${child.labelKey}`)}
                                  {child.id === "price-alert-stats" && priceAlertActiveCount > 0 && (
                                    <span className="nav-badge">{priceAlertActiveCount}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={item.id}
                        className={`nav-item${page === item.id ? " active" : ""}`}
                        onClick={() => {
                          setPage(item.id);
                          setNavOpen(false);
                        }}
                      >
                        <span style={{ color: "#9ca3af", display: "flex", alignItems: "center", flexShrink: 0 }}>{Ic[item.icon]}</span>
                        {navItemLabel(item.id, t)}
                        {item.id === "carts" && cartStats.pending > 0 && <span className="nav-badge">{cartStats.pending}</span>}
                      </div>
                    );
                  })}
              </div>
            ))}
          </nav>
          <LanguageSelector />
          <div style={{padding:"0 8px 0",marginTop:20,marginBottom:20}}>
            <a href="https://app.carthub.cloud/api/plugin/download" download
              style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"9px 10px",borderRadius:12,border:"1px solid #0A85CF",background:"#0A85CF",color:"#fff",fontSize:13,fontWeight:500,textDecoration:"none",transition:"all .15s",cursor:"pointer",boxShadow:"var(--shadow-module)"}}
              onMouseOver={e=>{e.currentTarget.style.background="#0878b8";e.currentTarget.style.borderColor="#0878b8";}}
              onMouseOut={e=>{e.currentTarget.style.background="#0A85CF";e.currentTarget.style.borderColor="#0A85CF";}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {t("sidebar.downloadPlugin", { version: pluginInfo.version })}
            </a>
            <div style={{textAlign:"center",fontSize:11,color:"#9ca3af",marginTop:5}}>
              {pluginInfo.updated_at ? t("sidebar.updatedOn", { date: new Date(pluginInfo.updated_at).toLocaleDateString(dateLocaleForAppLang(i18n.language), { day: "numeric", month: "short", year: "numeric" }) }) : ""}
            </div>
          </div>
          <div className="sidebar-foot">
            <div className="merchant-row">
              <div className="merchant-av">{av(shop?.name)}</div>
              <div style={{flex:1,minWidth:0}}>
                <div className="merchant-name">{shop?.name}</div>
                <div className="merchant-domain">{shop?.domain}</div>
              </div>
              <button className="logout-btn" onClick={logout} title={t("logoutTitle")}>{Ic.logout}</button>
            </div>
          </div>
        </aside>

        {/* ── MAIN ── */}
        <div className="main">
          <div className="topbar">
            <button
              type="button"
              className="mobile-nav-toggle"
              aria-label={t("aria.openNav")}
              aria-expanded={navOpen}
              onClick={()=>setNavOpen((o)=>!o)}
            >
              {Ic.menu}
            </button>
            <div className="topbar-title">{t(`pageTitle.${pageTitleKey(page)}`)}</div>
            {page==="carts"&&<><span className="pill">{t("pill.total", { count: cartStats.total })}</span><span className="pill green">{t("pill.recovered", { count: cartStats.recovered })}</span>{cartStats.organic>0&&<span className="pill" style={{background:'#e3f2fd',color:'#1565c0',borderColor:'#90caf9'}}>{t("pill.organic", { count: cartStats.organic })}</span>}</>}
            {page==="stats"&&<span className="pill">{t("pill.recoveryRate", { rate: cartStats.rate })}</span>}
            {page==="stats-popup"&&<span className="pill">{t("pill.popupStatsHint")}</span>}
            {page==="brands"&&<span className="pill">{t("pill.brandsConfigured", { count: brandsCount })}</span>}
            <MerchantPlanPills shop={shop} onChoosePlan={() => { setPage("subscription"); setNavOpen(false); }} />
          </div>
          <BillingAttentionBanner shop={shop} onGoSubscription={() => { setPage("subscription"); setNavOpen(false); }} />
          {/* ── SILENT MODE BANNER (apprendimento automatico) ── */}
          {shop?.silent_until && new Date(shop.silent_until) > new Date() && (()=>{
            const until = shop.silent_until;
            const daysLeft = Math.ceil((new Date(until)-new Date())/(1000*60*60*24));
            const hoursLeft = Math.ceil((new Date(until)-new Date())/(1000*60*60));
            const timeLabel = daysLeft > 1 ? t("banner.silent.timeDays", { count: daysLeft }) : t("banner.silent.timeHours", { count: Math.max(1, hoursLeft) });
            return (
              <div className="ch-app-banner ch-app-banner--silent" role="status">
                <span className="ch-app-banner__icon" aria-hidden>{Ic.bellOff}</span>
                <div className="ch-app-banner__body">
                  <span className="ch-app-banner__title">{t("banner.silent.title")}</span>
                  <span>{t("banner.silent.body", { time: timeLabel })}</span>
                </div>
                <button type="button" className="ch-app-banner__btn" onClick={deactivateSilentMode}>
                  {t("banner.silent.cta")}
                </button>
              </div>
            );
          })()}
          {/* ── PAUSA MANUALE BANNER ── */}
          {!!shop?.email_paused && (
            <div className="ch-app-banner ch-app-banner--pause" role="status">
              <span className="ch-app-banner__icon" aria-hidden>{Ic.pause}</span>
              <div className="ch-app-banner__body">
                <span className="ch-app-banner__title">{t("banner.pause.title")}</span>
                <span>{t("banner.pause.body")}</span>
              </div>
              <button type="button" className="ch-app-banner__btn" onClick={toggleEmailPause} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-flex' }}>{Ic.play}</span>
                {t("banner.pause.resume")}
              </button>
            </div>
          )}
          <div className="content">
            {page==="carts"        && <Carts shopId={shopId} shopDomain={shop?.domain || ""} currencyCode={shop?.store_currency || "EUR"} />}
            {page==="stats"        && <Stats shopId={shopId} currencyCode={shop?.store_currency || "EUR"} />}
            {page==="stats-popup"  && (
              <StatsPopupPage shopId={shopId} currencyCode={shop?.store_currency || "EUR"} />
            )}
            {page==="brands"            && <Brands shopId={shopId}/>}
            {page==="price-alert-stats" && (
              <PriceAlerts
                shopId={shopId}
                shopCurrency={shop?.store_currency || "EUR"}
                onOpenSetup={() => {
                  setSettingsTabHint("price-alert");
                  setPage("settings");
                }}
              />
            )}
            {page==="subscription" && <Subscription shop={shop} onSubscriptionSynced={applySubscriptionPayload} />}
            {page==="settings"     && (
              <Settings
                shop={shop}
                emailPaused={!!shop?.email_paused}
                onTogglePause={toggleEmailPause}
                onShopPatch={mergeShop}
                settingsTabHint={settingsTabHint}
                onSettingsTabHintConsumed={() => setSettingsTabHint(null)}
                onOpenPriceAlertStats={() => setPage("price-alert-stats")}
              />
            )}
          </div>
        </div>
      </div>
      {isMerchantTrialActive(shop) && (
        <>
          <TrialWelcomeModal
            open={trialWelcomeOpen}
            welcomeFlow
            variant={shop?.is_agency_managed ? "agency" : "direct"}
            onClose={() => setTrialWelcomeOpen(false)}
            onGoToSubscription={goToSubscription}
          />
          <TrialTrialBar
            variant={shop?.is_agency_managed ? "agency" : "direct"}
            onGoToSubscription={shop?.is_agency_managed ? undefined : goToSubscription}
          />
        </>
      )}
    </>
  );
}
